import { UF_BY_CODE, UF_KEYS } from '../constants';
import { changeSpaceToUnderline } from './changeSpaceToUnderline';

/** Padrões comuns para endereço sem número (Brasil). */
const SEM_NUMERO_REGEX = /^(s\s*\/?\s*n\.?\s*º?|s\/nº?|sn|sem\s*n[uú]mero|n\.?\s*a\.?|n\/a|0+)$/i;

export function isSemNumero(value: string): boolean {
  return SEM_NUMERO_REGEX.test(value.trim());
}

export function isValidAudienceNumberField(value: unknown): boolean {
  const numberValue = String(value ?? '');
  return Boolean(numberValue.trim()) && (isSemNumero(numberValue) || /\d/.test(numberValue));
}

/** Converte o valor do campo número para o formato numérico do backend (0 = sem número). */
export function parseAddressNumber(value: string): number {
  const trimmed = value.trim();
  if (!trimmed || isSemNumero(trimmed)) {
    return 0;
  }

  const digitsOnly = trimmed.replace(/[^\d]/g, '');
  if (digitsOnly) {
    return Number(digitsOnly);
  }

  const parsed = Number(trimmed);
  return Number.isNaN(parsed) ? 0 : parsed;
}

/** Remove sufixo de UF colado no nome da cidade (ex.: "São Paulo de Olivença – AM"). */
export function normalizeCityName(city: string): string {
  return city.replace(/\s*[–—-]\s*[A-Za-z]{2}\s*$/i, '').trim();
}

function resolveStateUf(state: string, stateUfCode?: string | number): string {
  if (stateUfCode !== undefined && stateUfCode !== '') {
    const code = Number(stateUfCode);
    if (!Number.isNaN(code) && UF_BY_CODE[code]) {
      return UF_BY_CODE[code];
    }
  }

  const trimmed = state.trim();
  if (trimmed.length === 2) {
    return trimmed.toUpperCase();
  }

  const fromName = UF_KEYS[changeSpaceToUnderline(trimmed)];
  if (fromName) {
    return fromName;
  }

  return trimmed;
}

export type AddressGeocodingParts = {
  street: string;
  number: string;
  neighborhood: string;
  city: string;
  state: string;
  stateUfCode?: string | number;
};

/**
 * Gera variações de endereço para geocoding.
 * Endereços sem número tentam primeiro sem o trecho do número (melhor taxa de acerto no Google).
 */
export function buildGeocodingAddressQueries(parts: AddressGeocodingParts): string[] {
  const uf = resolveStateUf(parts.state, parts.stateUfCode);
  const city = normalizeCityName(parts.city);
  const street = parts.street.trim();
  const neighborhood = parts.neighborhood.trim();
  const number = parts.number.trim();

  const withNumber = `${street}, ${number}, ${neighborhood}, ${city}, ${uf}, Brasil`;
  const withoutNumber = `${street}, ${neighborhood}, ${city}, ${uf}, Brasil`;

  if (isSemNumero(number)) {
    return [withoutNumber, withNumber];
  }

  return [withNumber, withoutNumber];
}

export function buildAddressCacheKey(parts: AddressGeocodingParts): string {
  return [
    parts.street,
    parts.number,
    parts.neighborhood,
    normalizeCityName(parts.city),
    resolveStateUf(parts.state, parts.stateUfCode),
  ].join('|');
}
